import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FreeKeyPublic } from '../../../services/freeKeyService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import StarRating from '../../../components/StarRating';
import { Eye, EyeOff } from 'lucide-react';

interface KeysTableProps {
    keys: FreeKeyPublic[];
    loading: boolean;
    periodType: string;
    canViewFull: boolean;
}

const ExpandableText: React.FC<{ text: string; maxLength?: number }> = ({ text, maxLength = 50 }) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation('common');

    if (!text || text.length <= maxLength) {
        return <span>{text || '-'}</span>;
    }

    return (
        <div className="flex flex-col items-start">
            <span className="break-words">{expanded ? text : `${text.substring(0, maxLength)}...`}</span>
            <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-blue-500 hover:text-blue-700 font-medium mt-1"
                type="button"
            >
                {expanded ? t('freeKeys.table.viewLess') : t('freeKeys.table.viewMore')}
            </button>
        </div>
    );
};

const ClickableLink: React.FC<{ url: string }> = ({ url }) => {
    if (!url) return <span>-</span>;
    const isValidUrl = url.startsWith('http://') || url.startsWith('https://');

    if (isValidUrl) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
                title={url}
            >
                {url}
            </a>
        );
    }
    return <span title={url}>{url}</span>;
};

export const KeysTable: React.FC<KeysTableProps> = ({ keys, loading, periodType, canViewFull }) => {
    const { t } = useTranslation('common');
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const stickyScrollbarRef = useRef<HTMLDivElement>(null);
    const [tableScrollWidth, setTableScrollWidth] = useState(0);

    // Sync sticky scrollbar with table scroll
    useEffect(() => {
        const wrap = tableWrapRef.current;
        const bar = stickyScrollbarRef.current;

        if (!wrap) return;

        const updateWidth = () => {
            if (wrap) setTableScrollWidth(wrap.scrollWidth);
        };

        // Wait for table to render
        const timer1 = setTimeout(updateWidth, 100);
        const timer2 = setTimeout(updateWidth, 500);

        if (bar) {
            const syncFromWrap = () => {
                if (bar && wrap) bar.scrollLeft = wrap.scrollLeft;
            };
            const syncFromBar = () => {
                if (wrap && bar) wrap.scrollLeft = bar.scrollLeft;
            };

            wrap.addEventListener("scroll", syncFromWrap, { passive: true });
            bar.addEventListener("scroll", syncFromBar, { passive: true });

            const ro = new ResizeObserver(updateWidth);
            ro.observe(wrap);
            window.addEventListener('resize', updateWidth);

            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
                wrap.removeEventListener("scroll", syncFromWrap);
                bar.removeEventListener("scroll", syncFromBar);
                window.removeEventListener('resize', updateWidth);
                ro.disconnect();
            };
        }
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [keys, loading]);

    if (loading) {
        return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
    }

    if (keys.length === 0) {
        return (
            <div className="p-12 text-center border-2 border-dashed rounded-lg bg-gray-50 text-gray-400">
                <p>{t('freeKeys.table.empty', { period: periodType })}</p>
            </div>
        );
    }

    return (
        <div className="relative border rounded-lg shadow-sm bg-white overflow-hidden">
            {/* Note for restricted view */}
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center text-sm">
                <span className="font-semibold text-gray-700">
                    {t('freeKeys.table.total', { count: keys.length })}
                </span>
                {!canViewFull && keys.length > 2 && (
                    <span className="text-amber-600 flex items-center gap-1 font-medium bg-amber-50 px-2 py-1 rounded border border-amber-100">
                        <EyeOff size={14} />
                        {t('freeKeys.table.restricted')}
                    </span>
                )}
            </div>

            <div
                ref={tableWrapRef}
                className="overflow-x-auto"
            >
                <Table className="min-w-[1200px]">
                    <TableHeader className="bg-gray-100">
                        <TableRow>
                            <TableHead className="w-[50px]">{t('freeKeys.table.stt')}</TableHead>
                            <TableHead className="min-w-[150px]">{t('freeKeys.table.corps')}</TableHead>
                            <TableHead className="min-w-[150px]">{t('freeKeys.table.sect')}</TableHead>
                            <TableHead className="min-w-[150px]">{t('freeKeys.table.viralKey')}</TableHead>
                            <TableHead className="min-w-[100px]">{t('freeKeys.table.rpm')}</TableHead>
                            <TableHead className="min-w-[200px]">{t('freeKeys.table.key')}</TableHead>
                            <TableHead className="min-w-[100px]">{t('freeKeys.table.views')}</TableHead>
                            <TableHead className="min-w-[100px]">{t('freeKeys.table.periodType')}</TableHead>
                            <TableHead className="min-w-[100px]">{t('freeKeys.table.periodValue')}</TableHead>
                            <TableHead className="min-w-[200px]">{t('freeKeys.table.prompt')}</TableHead>
                            <TableHead className="min-w-[200px]">{t('freeKeys.table.competitor')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {keys.map((key, index) => {
                            const isHidden = key.key === '***';
                            // Highlight first 2 rows if !canViewFull to encourage signup? Or just normal
                            // The logic says if !canViewFull only first 2 are shown.
                            // Actually the API returns '***' for hidden ones.
                            return (
                                <TableRow key={key.id || index} className="hover:bg-slate-50 transition-colors">
                                    <TableCell>{key.stt}</TableCell>
                                    <TableCell>{isHidden ? '-' : <ExpandableText text={key.quan_doan || ''} maxLength={20} />}</TableCell>
                                    <TableCell>{isHidden ? '-' : <ExpandableText text={key.mon_phai || ''} maxLength={20} />}</TableCell>
                                    <TableCell>{isHidden ? '-' : <ExpandableText text={key.viral_key || ''} maxLength={25} />}</TableCell>
                                    <TableCell>{isHidden ? '-' : <StarRating value={key.rpm || 0} />}</TableCell>
                                    <TableCell className="font-mono text-xs bg-gray-50">
                                        {isHidden ? (
                                            <span className="text-gray-400 select-none">••••••••</span>
                                        ) : (
                                            <ExpandableText text={key.key || ''} maxLength={30} />
                                        )}
                                    </TableCell>
                                    <TableCell className="font-semibold text-blue-600">
                                        {isHidden ? '-' : key.views?.toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        {/* Map period code to Label if needed or just use type */}
                                        {t(`freeKeys.period.${key.period_type}` as any)}
                                    </TableCell>
                                    <TableCell>{key.period_value}</TableCell>
                                    <TableCell>{isHidden ? '-' : <ExpandableText text={key.prompt || ''} maxLength={40} />}</TableCell>
                                    <TableCell>{isHidden ? '-' : <ClickableLink url={key.kenh_doi_thu || ''} />}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Sticky Horizontal Scrollbar */}
            <div
                ref={stickyScrollbarRef}
                className="fixed bottom-0 left-0 right-0 h-4 bg-gray-100 border-t border-gray-300 z-50 overflow-x-auto opacity-90 hover:opacity-100 transition-opacity"
                style={{ display: tableScrollWidth > 0 ? 'block' : 'none' }}
            >
                <div style={{ width: Math.max(tableScrollWidth, 100), height: 1 }} />
            </div>
        </div>
    );
};
